import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import SimpleBar from 'simplebar-react';
import './CheckboxSelect.scss';

export interface CheckboxSelectOption<T extends string | number> {
  value: T;
  /** Tekst wiersza i podstawa wyszukiwania; `content` może go zastąpić wizualnie. */
  label: string;
  /** Bogatszy wiersz (ikona modelu, kłódka, badge) — bez niego rysujemy sam `label`. */
  content?: ReactNode;
  /** Wiersz nie do zaznaczenia (np. model spoza pakietu). */
  disabled?: boolean;
  /**
   * Always selected and not un-tickable (e.g. the own brand in the brand picker): the checkbox is
   * disabled, but the row is NOT dimmed like `disabled` — it should read as highlighted.
   */
  fixed?: boolean;
  /** Akcja pokazywana na hover, np. „only". */
  action?: ReactNode;
}

interface CheckboxSelectProps<T extends string | number> {
  selected: T[];
  onChange: (next: T[]) => void;
  options: CheckboxSelectOption<T>[];
  /**
   * "All" row at the top of the list — ticks everything, un-ticking keeps the first item.
   * Without a label there is no row (a list with a selection cap cannot offer "select all").
   */
  allLabel?: string;
  allContent?: ReactNode;
  /** Short hint above the list, e.g. the selection cap. */
  hint?: string;
  /** Co pokazać na przycisku: nazwa przy jednym wyborze, licznik przy kilku. */
  triggerContent: ReactNode;
  /** Podany placeholder włącza pole wyszukiwania w panelu. */
  searchPlaceholder?: string;
  /** Podana wysokość zwija listę do przewijanego obszaru (SimpleBar). Bez niej panel rośnie w pion. */
  maxListHeight?: number;
  className?: string;
}

/**
 * Lista pozycji. Z podaną wysokością zawijamy `<ul>` w SimpleBar (ten sam pasek, co w panelu
 * bocznym); bez niej nie owijamy niczym — krótka lista nie potrzebuje kontenera przewijania.
 */
const ListWrapper: React.FC<{ maxHeight?: number; children: ReactNode }> = ({
  maxHeight,
  children,
}) =>
  maxHeight ? (
    <SimpleBar style={{ maxHeight }} autoHide={false}>
      <ul>{children}</ul>
    </SimpleBar>
  ) : (
    <ul>{children}</ul>
  );

/**
 * Rozwijana lista z checkboxami: wiersz „wszystko", pozycje z zaznaczaniem i opcjonalne szukanie.
 * Jedna mechanika dla filtra modeli w panelu i wyboru promptów w oknie raportu — dzięki temu obie
 * kontrolki wyglądają i zachowują się tak samo (łącznie z zakazem odznaczenia ostatniej pozycji).
 */
const CheckboxSelect = <T extends string | number>({
  selected,
  onChange,
  options,
  allLabel,
  allContent,
  hint,
  triggerContent,
  searchPlaceholder,
  maxListHeight,
  className = '',
}: CheckboxSelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [phrase, setPhrase] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectableValues = useMemo(
    () => options.filter((option) => !option.disabled).map((option) => option.value),
    [options]
  );
  const isAllSelected = selected.length >= selectableValues.length;

  const visibleOptions = useMemo(() => {
    const needle = phrase.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, phrase]);

  const toggle = (value: T, isChecked: boolean) => {
    if (isChecked) {
      // Lista bez żadnej pozycji nie ma sensu — ostatnia zostaje.
      if (selected.length <= 1) return;
      onChange(selected.filter((entry) => entry !== value));
      return;
    }
    onChange([...selected, value]);
  };

  const toggleAll = () => {
    onChange(isAllSelected ? selectableValues.slice(0, 1) : [...selectableValues]);
  };

  return (
    <div className={`checkbox-select ${className}`.trim()} ref={dropdownRef}>
      <div
        className={`checkbox-select__trigger ${isOpen ? 'checkbox-select__trigger--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {triggerContent}
        <div className="checkbox-select__chevron">
          <span className="ico--select-arrow" aria-hidden="true" />
        </div>
      </div>

      {isOpen && (
        <div className="checkbox-select__panel">
          {searchPlaceholder && (
            <div className="checkbox-select__search">
              <InputText
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={searchPlaceholder}
                autoFocus
              />
            </div>
          )}
          {hint && <div className="checkbox-select__hint">{hint}</div>}
          <ListWrapper maxHeight={maxListHeight}>
            {/* Wiersz „wszystko" nie znika przy szukaniu — to skrót do pełnego zakresu, nie pozycja. */}
            {allLabel && (
              <li
                className={`checkbox-select__item ${
                  isAllSelected ? 'checkbox-select__item--selected' : ''
                }`}
                onClick={toggleAll}
              >
                <Checkbox checked={isAllSelected} />
                {allContent ?? <span className="checkbox-select__label">{allLabel}</span>}
              </li>
            )}
            {visibleOptions.map((option) => {
              const isChecked = selected.includes(option.value);

              return (
                <li
                  key={option.value}
                  className={`checkbox-select__item ${
                    isChecked ? 'checkbox-select__item--selected' : ''
                  } ${option.disabled ? 'checkbox-select__item--locked' : ''}`}
                  onClick={() => {
                    if (!option.disabled && !option.fixed) toggle(option.value, isChecked);
                  }}
                >
                  <Checkbox
                    checked={isChecked}
                    disabled={!!option.disabled || !!option.fixed || (isChecked && selected.length <= 1)}
                  />
                  {option.content ?? <span className="checkbox-select__label">{option.label}</span>}
                  {option.action}
                </li>
              );
            })}
          </ListWrapper>
        </div>
      )}
    </div>
  );
};

export default CheckboxSelect;
